import { useEffect, useRef } from 'react'

import { useBraze } from '@/context/brazeContext'

import { useOptimizelyFlagsInfo } from './useOptimizelyFlagsInfo'

const AB_EXPERIMENTS_ATTRIBUTE = 'ab_experiments'

/**
 * Mirrors the user's Optimizely experiment→variation assignments onto the
 * anonymous Braze profile as one nested custom attribute (`ab_experiments`), so
 * campaigns/modals can segment by experiment. Runs only once Braze is
 * initialised (which already requires marketing consent). The full assignment
 * map is re-sent whenever a new experiment lands, and de-duped so an unchanged
 * map is never sent twice.
 */
export const useBrazeExperimentSync = () => {
  const { initialized, setUserAttribute } = useBraze()
  const { assignments } = useOptimizelyFlagsInfo()
  const lastSent = useRef<string | null>(null)

  useEffect(() => {
    // A resolved empty map still overwrites, clearing stale assignments when every
    // experiment is held back.
    if (!initialized || !assignments) return
    const serialized = JSON.stringify(assignments)
    if (serialized === lastSent.current) return
    lastSent.current = serialized
    setUserAttribute(AB_EXPERIMENTS_ATTRIBUTE, assignments)
  }, [initialized, assignments, setUserAttribute])
}
